import Api from "./Api";
import { store } from "App";

// Live attendance stream (SSE).
//
// EventSource can't set headers, and the router needs Authorization + orgurl, so
// this reads the text/event-stream with fetch + ReadableStream and parses the
// frames by hand. Reconnect/backoff is also ours to do - fetch gives none of it.

const ENDPOINT = "/attendance-v3/live";
const RETRY_MIN_MS = 1000;
const RETRY_MAX_MS = 30000;

// Api is the exported singleton, so its _headers/_base_url are the live config.
// Authorization can still be "" before the first request, so fall back to the
// store the same way Api.send() does.
function streamHeaders() {
  const session = store.getState()["session"] || {};
  const fromApi = Api._headers?.Authorization;
  const token = fromApi || (session.authenticated ? `Bearer ${session.user.accessToken}` : "");
  return {
    Authorization: token,
    orgurl: Api._headers?.orgurl,
    Accept: "text/event-stream",
  };
}

// splits an SSE buffer into complete events; a blank line dispatches one
function parseFrames(buffer) {
  const frames = [];
  // events are separated by a blank line, tolerating \r\n
  const parts = buffer.split(/\r?\n\r?\n/);
  // the last piece may be a partial event, so hand it back to the caller
  const rest = parts.pop();

  for (const part of parts) {
    const event = { type: "message", data: "", id: null };
    for (const line of part.split(/\r?\n/)) {
      if (!line || line.startsWith(":")) continue; // comment / heartbeat
      const colon = line.indexOf(":");
      const field = colon === -1 ? line : line.slice(0, colon);
      // one optional space after the colon is part of the format
      let value = colon === -1 ? "" : line.slice(colon + 1);
      if (value.startsWith(" ")) value = value.slice(1);

      if (field === "data") event.data += (event.data ? "\n" : "") + value;
      else if (field === "event") event.type = value;
      else if (field === "id") event.id = value;
    }
    if (event.data) frames.push(event);
  }

  return { frames, rest };
}

// Opens the stream and calls onPunch for each punch event.
// Returns a stop() function - call it on unmount.
export function subscribeToLiveAttendance({ onPunch, onStatus }) {
  let stopped = false;
  let controller = null;
  let retryMs = RETRY_MIN_MS;
  let retryTimer = null;

  const setStatus = (s) => { if (onStatus && !stopped) onStatus(s); };

  async function connect() {
    if (stopped) return;

    controller = new AbortController();

    try {
      const res = await fetch(`${Api._base_url}/api${ENDPOINT}`, {
        method: "GET",
        headers: streamHeaders(),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        throw new Error(`stream failed with ${res.status}`);
      }

      setStatus("connected");
      // a successful connect resets backoff, so a long-lived stream that drops
      // once reconnects promptly instead of inheriting an old long delay
      retryMs = RETRY_MIN_MS;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const { frames, rest } = parseFrames(buffer);
        buffer = rest;

        for (const frame of frames) {
          // the server also sends type "ping" to hold the connection open
          if (frame.type !== "punch") continue;
          try {
            onPunch(JSON.parse(frame.data));
          } catch (e) {
            // one malformed frame must not tear down the stream
            console.log("[live-attendance] bad frame", e.message);
          }
        }
      }

      // the server closed the response - treat as a drop and reconnect
      throw new Error("stream ended");
    } catch (err) {
      if (stopped || err.name === "AbortError") return;
      setStatus("reconnecting");
      retryTimer = setTimeout(connect, retryMs);
      // exponential backoff so a router restart isn't hammered by every dashboard
      retryMs = Math.min(retryMs * 2, RETRY_MAX_MS);
    }
  }

  connect();

  return function stop() {
    stopped = true;
    clearTimeout(retryTimer);
    if (controller) controller.abort();
  };
}
